import { Text } from "@assetwatch/ui";
import type { AssetId } from "../../lib/workIds";

/**
 * Pure presentational key-value rendering of an Asset/Location's Details
 * fields. Shared by the Asset Detail side sheet (IWA-19838) and the full
 * Asset Detail page (IWA-19840) — keep this component free of side-sheet-only
 * concerns (no Drawer, no routing, no mutation actions).
 *
 * Flat uppercase-label-over-value fields (no per-field card/border), matching
 * the POC's Details card. Follows the same "value?.trim() ? value :
 * 'Unavailable'" convention already established — never invents a value for
 * a missing source field.
 */
export type AssetDetailFieldsProps = {
  asset: {
    assetId: AssetId | null;
    name: string;
    code: string;
    kind: "ASSET" | "LOCATION" | null;
    sapFunctionalLocationId: string | null;
    manufacturer: string | null;
    model: string | null;
  };
  physicalLocation: { name: string; code: string } | null;
  parentAsset: { code: string; name: string } | null;
  hierarchyPath: { id: number; name: string }[];
  /**
   * "stacked" (default) is a single column, sized for the narrow side
   * sheet. "grid" spreads fields across a responsive multi-column grid for
   * the full detail page, where a single narrow column would look sparse.
   */
  layout?: "stacked" | "grid";
};

function Field({ label, value }: { label: string; value: string | null }) {
  const displayValue = value?.trim() ? value : "Unavailable";
  return (
    <div className="flex flex-col gap-(--spacing-8)">
      <span className="font-(number:--font-weight-semibold) text-(length:--font-size-label-small) uppercase tracking-(--font-letter-spacing-default) text-text-subtle">
        {label}
      </span>
      <Text size="md">{displayValue}</Text>
    </div>
  );
}

export function AssetDetailFields({
  asset,
  physicalLocation,
  parentAsset,
  hierarchyPath,
  layout = "stacked",
}: AssetDetailFieldsProps) {
  return (
    <div className="flex flex-col gap-(--spacing-16)">
      {hierarchyPath.length > 0 ? (
        <Text size="sm" tone="subtle">
          {hierarchyPath.map((ancestor) => ancestor.name).join(" / ")}
        </Text>
      ) : null}
      {/* Order follows the POC Details card (Asset ID, Location,
          Manufacturer, Model first), limited to fields the imported record
          actually carries. */}
      <div
        className={
          layout === "grid"
            ? "grid gap-x-(--spacing-24) gap-y-(--spacing-20) sm:grid-cols-2 lg:grid-cols-4"
            : "grid gap-(--spacing-16)"
        }
      >
        <Field label="Asset ID" value={asset.code} />
        <Field
          label="Physical Location"
          value={
            physicalLocation
              ? `${physicalLocation.name} (${physicalLocation.code})`
              : null
          }
        />
        <Field label="Manufacturer" value={asset.manufacturer} />
        <Field label="Model" value={asset.model} />
        <Field label="Name" value={asset.name} />
        <Field
          label="Kind"
          value={
            asset.kind === "ASSET"
              ? "Equipment"
              : asset.kind === "LOCATION"
                ? "Location"
                : null
          }
        />
        <Field
          label="SAP Functional Location ID"
          value={asset.sapFunctionalLocationId}
        />
        <Field
          label="Parent Asset ID"
          value={parentAsset ? parentAsset.code : null}
        />
      </div>
    </div>
  );
}
